import React, { useState, useRef } from 'react';
import { X, Check, Eraser, PenTool, Send, Loader2, Paperclip, FileText } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import Button from './Button';

// --- MODALE SIGNATURE ---
export const SignaturePad = ({ onSave, onCancel }) => {
  const sigCanvas = useRef({});
  
  const clear = () => sigCanvas.current.clear();
  
  const save = () => {
      if(sigCanvas.current.isEmpty()) { 
          alert("Veuillez signer avant de valider."); 
          return; 
      }
      // Récupère l'image en base64 (PNG)
      onSave(sigCanvas.current.getTrimmedCanvas().toDataURL('image/png'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><PenTool size={18} className="text-blue-600"/> Signature Client</h3>
            <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
        </div>
        <div className="p-4 bg-slate-100 relative">
            <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 shadow-sm overflow-hidden h-64 relative">
                <SignatureCanvas 
                    ref={sigCanvas} 
                    penColor="black" 
                    canvasProps={{className: 'w-full h-full'}} 
                />
                <div className="absolute bottom-2 right-2 text-[10px] text-slate-300 pointer-events-none">Signez dans le cadre</div>
            </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-white">
            <button onClick={clear} className="text-xs font-bold text-slate-500 hover:text-red-500 flex items-center gap-1"><Eraser size={14}/> Effacer</button>
            <div className="flex gap-2">
                <Button variant="secondary" onClick={onCancel}>Annuler</Button>
                <Button variant="primary" onClick={save} icon={Check}>Valider</Button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- MODALE EMAIL AVEC PIÈCE JOINTE ---
export const EmailModal = ({ client, docNumber, onClose, onSend }) => {
    const [subject, setSubject] = useState(`Document ${docNumber}`);
    const [message, setMessage] = useState(`Bonjour,\n\nVeuillez trouver ci-joint le document ${docNumber}.\n\nCordialement.`);
    const [isSending, setIsSending] = useState(false);
    const [attachment, setAttachment] = useState(null); // État pour le fichier PDF

    // Gestion de l'upload de fichier
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Limite de taille (2Mo) pour ne pas bloquer l'envoi email
            if (file.size > 1024 * 1024 * 2) { 
                alert("Le fichier est trop lourd (max 2Mo).");
                return;
            }
            
            // Conversion du fichier en Base64 (texte) pour l'envoi via l'extension Firebase
            const reader = new FileReader();
            reader.onloadend = () => {
                // On enlève le préfixe "data:application/pdf;base64," pour ne garder que la donnée brute
                const base64String = reader.result.split(',')[1];
                setAttachment({
                    filename: file.name,
                    content: base64String,
                    type: file.type, // ex: 'application/pdf'
                    disposition: 'attachment'
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSendClick = async () => {
        setIsSending(true);
        // On passe l'objet attachment à la fonction parente (Editor.js)
        await onSend({ subject, message, attachment });
        setIsSending(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col transform transition-all scale-100">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white flex justify-between items-center">
                    <h3 className="font-bold text-lg flex items-center gap-2"><Send size={18}/> Envoyer le document</h3>
                    <button onClick={onClose} className="text-blue-100 hover:text-white bg-white/10 p-1 rounded-full hover:bg-white/20 transition"><X size={18}/></button>
                </div>
                
                <div className="p-6 space-y-4">
                    {/* Destinataire */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Destinataire</label>
                        <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-700 font-medium text-sm cursor-not-allowed">
                            {client.email || "Pas d'email renseigné pour ce client"}
                        </div>
                    </div>

                    {/* Sujet */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Sujet</label>
                        <input 
                            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 text-sm transition" 
                            value={subject} 
                            onChange={e => setSubject(e.target.value)} 
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Message</label>
                        <textarea 
                            rows="5" 
                            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 text-sm transition resize-none" 
                            value={message} 
                            onChange={e => setMessage(e.target.value)} 
                        />
                    </div>

                    {/* ZONE UPLOAD PDF (NOUVEAU) */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Pièce jointe supplémentaire (PDF)</label>
                        {!attachment ? (
                            <label className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-300 rounded-xl p-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition text-slate-400 hover:text-blue-600">
                                <Paperclip size={18}/>
                                <span className="text-xs font-bold">Cliquez pour ajouter un PDF (max 2Mo)</span>
                                <input type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
                            </label>
                        ) : (
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-3 animate-in zoom-in-95">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white p-2 rounded-lg text-red-500 shadow-sm border border-blue-100"><FileText size={16}/></div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-blue-800 truncate max-w-[200px]">{attachment.filename}</span>
                                        <span className="text-[10px] text-blue-400">Prêt à l'envoi</span>
                                    </div>
                                </div>
                                <button onClick={() => setAttachment(null)} className="text-blue-400 hover:text-red-500 p-1.5 hover:bg-white rounded-full transition"><X size={16}/></button>
                            </div>
                        )}
                    </div>

                </div>

                <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button variant="primary" onClick={handleSendClick} disabled={!client.email || isSending} icon={isSending ? Loader2 : Send}>
                        {isSending ? 'Envoi...' : 'Envoyer'}
                    </Button>
                </div>
            </div>
        </div>
    );
};